import { ReadonlyVec2 } from 'gl-matrix';
import { TextureView } from '../view/TextureView';
import {
    EventHandlerResult,
    EventHandlerResultType,
} from './EventHandlerResult';
import { ModifierKeys } from './ModifierKeys';
import { PlaneView } from '../view/views/PlaneView';

/**
 * Super class for all the tool actions
 */
export abstract class ToolAction {
    /**
     * MousePress event that was caught by the canvas and propagated by {@link ToolManager} to this {@link ToolAction}.
     * @param _button The button that triggered the event
     * @param _modifierKeys {@link ModifierKeys} that were active at the time of the event
     * @param _coordinates The mouse position in {@link ReadonlyVec2 client pixel coordinates}, in between [0, pixelSize - 1], at the time of the event
     * @param _view The {@link TextureView} on which the event occured
     * @returns An {@link EventHandlerResult} that informs the {@link ToolManager}
     */
    public onMousePress(
        _button: number,
        _modifierKeys: ModifierKeys,
        _coordinates: ReadonlyVec2,
        _view: TextureView
    ): EventHandlerResult {
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    /**
     * MouseRelease event that was caught by the canvas and propagated by {@link ToolManager} to this {@link ToolAction}.
     * @param _button The button that triggered the event
     * @param _coordinates The mouse position in {@link ReadonlyVec2 client pixel coordinates}, in between [0, pixelSize - 1], at the time of the event
     * @param _view The {@link TextureView} on which the event occured
     * @returns An {@link EventHandlerResult} that informs the {@link ToolManager}
     */
    public onMouseRelease(
        _button: number,
        _coordinates: ReadonlyVec2,
        _view: TextureView
    ): EventHandlerResult {
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    /**
     * MouseMove event that was caught by the canvas and propagated by {@link ToolManager} to this {@link ToolAction}.
     * @param _coordinates The mouse position in {@link ReadonlyVec2 client pixel coordinates}, in between [0, pixelSize - 1], at the time of the event
     * @param _buttons The mouse buttons that are down during the event
     * @param _view The {@link TextureView} on which the event occured
     * @returns An {@link EventHandlerResult} that informs the {@link ToolManager}
     */
    public onMouseMove(
        _coordinates: ReadonlyVec2,
        _buttons: number,
        _view: TextureView
    ): EventHandlerResult {
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    /**
     * KeyPress event that was caught by the canvas and propagated by {@link ToolManager} to this {@link ToolAction}.
     * @param _key The key that triggered the event
     * @param _modifierKeys {@link ModifierKeys} that were active at the time of the event
     * @param _view The {@link TextureView} on which the event occured
     * @returns An {@link EventHandlerResult} that informs the {@link ToolManager}
     */
    public onKeyPress(
        _key: string,
        _modifierKeys: ModifierKeys,
        _view: TextureView
    ): EventHandlerResult {
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    /**
     * MouseWheel event that was caught by the canvas and propagated by {@link ToolManager} to this {@link ToolAction}.
     * @param _delta The direction of the MouseWheel event.
     * -1 if the wheel was scrolled forwards, 1 if the wheel was scrolled backwards.
     * @param _modifierKeys {@link ModifierKeys} that were active at the time of the event
     * @param _coordinates The mouse position in {@link ReadonlyVec2 client pixel coordinates}, in between [0, pixelSize - 1], at the time of the event
     * @param _view The {@link TextureView} on which the event occured
     * @returns An {@link EventHandlerResult} that informs the {@link ToolManager}
     */
    public onMouseWheel(
        _delta: 1 | -1,
        _modifierKeys: ModifierKeys,
        _coordinates: ReadonlyVec2,
        _view: PlaneView
    ): EventHandlerResult {
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    /**
     * Method to request the {@link Cursor} from the {@link ToolAction}.
     * @param view Optional parameter of the currently active {@link TextureView}.
     * The {@link TextureView} is provided as a parameter as {@link ToolAction} may return a different type of cursor based on the view.
     */
    //public getCursor?(view?: TextureView): Cursor | undefined;
}
